import { useState, type FC } from 'react';
import { Button, InlineNotification, Tag, TextInput } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import type { WellborePoint, WellborePointEdit } from 'types/api-responses';
import {
  formatFeet,
  parseTypedNumber,
  toDecimalField,
  wellborePointName,
} from './utils';
import styles from './edit-wellbore-point-panel.module.scss';

export interface EditWellborePointPanelProps {
  point: WellborePoint;
  /** The elevation TVDSS is measured from; null when no datum resolves. */
  datumElevationFt: number | null;
  saving: boolean;
  onClose: () => void;
  /** Commit the edit. Only what changed is sent. */
  onSave: (values: WellborePointEdit) => void;
}

/**
 * The point editor, opened from a row's Edit point. Position is editable unless pinned;
 * TVD and TVDSS are one measurement through the datum, so typing either recomputes the
 * other and only TVD is sent. A typed depth anchors the re-derivation.
 * @param {EditWellborePointPanelProps} props The point, the datum, save state and handlers.
 * @return {JSX.Element} The panel.
 */
export const EditWellborePointPanel: FC<EditWellborePointPanelProps> = ({
  point,
  datumElevationFt,
  saving,
  onClose,
  onSave,
}) => {
  // Coordinates seed at six decimals and depths at two, as the table shows them.
  const [latitude, setLatitude] = useState(toDecimalField(point.latitude, 6));
  const [longitude, setLongitude] = useState(
    toDecimalField(point.longitude, 6)
  );
  const [tvd, setTvd] = useState(toDecimalField(point.tvdFt, 2));
  const [tvdss, setTvdss] = useState(toDecimalField(point.tvdssFt, 2));

  // Every source but the operator's own edit is someone's record of where this point
  // came from, and the pill says so.
  const identified = point.source !== 'user_derived';
  const groupTitle = (title: string) => (
    <span className={styles.fieldGroupTitleRow}>
      <span className={styles.fieldGroupTitle}>{title}</span>
      {identified && (
        <Tag type="gray" size="sm" className={styles.identifiedTag}>
          Identified
        </Tag>
      )}
    </span>
  );

  const lat = parseTypedNumber(latitude);
  const lon = parseTypedNumber(longitude);
  const tvdValue = parseTypedNumber(tvd);
  const latInvalid = lat === null || lat < -90 || lat > 90;
  const lonInvalid = lon === null || lon < -180 || lon > 180;
  const tvdInvalid = tvd.trim() !== '' && tvdValue === null;

  // Writing either depth rewrites the other through the datum; with no datum each
  // simply holds what was typed.
  const setDepthFromTvd = (raw: string) => {
    setTvd(raw);
    const value = parseTypedNumber(raw);
    if (datumElevationFt === null || value === null) return;
    setTvdss(String(Math.round((datumElevationFt - value) * 100) / 100));
  };
  const setDepthFromTvdss = (raw: string) => {
    setTvdss(raw);
    const value = parseTypedNumber(raw);
    if (datumElevationFt === null || value === null) return;
    setTvd(String(Math.round((datumElevationFt - value) * 100) / 100));
  };

  // Against the fields as seeded, not the raw values: those carry more digits than the
  // fields show, so an untouched field would otherwise read as a change and Save would
  // write the rounded value back, a derived depth as a typed one.
  const movedPosition =
    lat !== parseTypedNumber(toDecimalField(point.latitude, 6)) ||
    lon !== parseTypedNumber(toDecimalField(point.longitude, 6));
  const changedDepth =
    tvdValue !== parseTypedNumber(toDecimalField(point.tvdFt, 2));
  const canSave =
    !saving &&
    !latInvalid &&
    !lonInvalid &&
    !tvdInvalid &&
    ((movedPosition && !point.locationLocked) || changedDepth);

  const handleSave = () => {
    const values: WellborePointEdit = {};
    if (
      movedPosition &&
      !point.locationLocked &&
      lat !== null &&
      lon !== null
    ) {
      values.latitude = lat;
      values.longitude = lon;
    }
    if (changedDepth) values.tvd_ft = tvdValue;
    onSave(values);
  };

  return (
    <div className={styles.panel} aria-label="Edit wellbore point">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Edit wellbore point</h2>
          <p className={styles.subtitle}>Editing {wellborePointName(point)}</p>
        </div>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close editor"
          onClick={onClose}
        />
      </div>

      <div className={styles.body}>
        <div className={styles.fieldGroup}>{groupTitle('Location')}</div>
        {point.locationLocked && (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            className={styles.lockNote}
            title="This point's position is locked."
            subtitle="Unlock it on the map to move it. Its depth can still be set here."
          />
        )}
        <TextInput
          id="edit-point-latitude"
          labelText="Latitude"
          helperText="Decimal degrees"
          inputMode="decimal"
          value={latitude}
          disabled={saving || point.locationLocked}
          invalid={latInvalid}
          invalidText="Latitude must be between -90 and 90."
          onChange={(event) => setLatitude(event.target.value)}
        />
        <TextInput
          id="edit-point-longitude"
          labelText="Longitude"
          helperText="Decimal degrees"
          inputMode="decimal"
          value={longitude}
          disabled={saving || point.locationLocked}
          invalid={lonInvalid}
          invalidText="Longitude must be between -180 and 180."
          onChange={(event) => setLongitude(event.target.value)}
        />

        <div className={styles.fieldGroup}>{groupTitle('Depth')}</div>
        {/* Along-hole, and a property of the path rather than of this point. */}
        <dl className={styles.readOnly}>
          <div className={styles.readOnlyRow}>
            <dt>Measured depth</dt>
            <dd>{formatFeet(point.measuredDepthFt)}</dd>
            <span className={styles.hint}>Derived from the whole path</span>
          </div>
        </dl>
        <TextInput
          id="edit-point-tvd"
          labelText="Depth (TVD)"
          helperText="Feet below datum"
          placeholder="Not on record"
          inputMode="decimal"
          value={tvd}
          disabled={saving}
          invalid={tvdInvalid}
          invalidText="Enter a valid depth in feet."
          onChange={(event) => setDepthFromTvd(event.target.value)}
        />
        <TextInput
          id="edit-point-tvdss"
          labelText="TVDSS"
          helperText={
            datumElevationFt === null
              ? 'Feet subsea — no datum resolves, so the two are independent'
              : 'Feet subsea — kept in step with the depth above'
          }
          placeholder="Not on record"
          inputMode="decimal"
          value={tvdss}
          disabled={saving}
          onChange={(event) => setDepthFromTvdss(event.target.value)}
        />
        {point.depthSource === 'user' && (
          <Button
            kind="ghost"
            size="sm"
            disabled={saving}
            onClick={() => onSave({ clear_typed_depth: true })}
          >
            Use the derived depth instead
          </Button>
        )}
        <p className={styles.note}>
          A typed depth holds this point while the rest of the path is
          re-derived around it.
        </p>
      </div>

      <div className={styles.footer}>
        <Button kind="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button kind="primary" disabled={!canSave} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};
